(ns ui.view.grid-opensearch.utils
  (:require [cljs.reader :as edn]
            [clojure.string :as string]
            [common.currencies :as currencies]
            [frontend.state.permissions :as f-permissions]
            [frontend.utils :as fu]
            [frontend.zap-time :as zap-time]
            [oops.core :as oops]
            [ui.view.opensearch-value.currency :as currency]
            [ui.view.opensearch-value.format :as value-format]))

(defn parse-edn-string
  [value]
  (when (string? value)
    (try
      (edn/read-string value)
      (catch :default _
        nil))))

(defn- parse-stringified-multi-value
  [value]
  (when (string? value)
    (let [trimmed (string/trim value)]
      (when (or (and (string/starts-with? trimmed "[")
                     (string/ends-with? trimmed "]"))
                (and (string/starts-with? trimmed "#{")
                     (string/ends-with? trimmed "}")))
        (when-let [parsed (parse-edn-string trimmed)]
          (when (and (coll? parsed)
                     (not (map? parsed)))
            (vec parsed)))))))

(defn- char-fragment?
  [value]
  (or (char? value)
      (and (string? value)
           (= 1 (count value)))))

(defn- collapse-char-seq
  [values]
  (if (and (> (count values) 1)
           (every? char-fragment? values))
    [(apply str values)]
    values))

(defn normalize-multi-values
  [value]
  (cond
    (js/Array.isArray value)
    (-> value js->clj vec collapse-char-seq)

    (sequential? value)
    (-> value vec collapse-char-seq)

    (and (coll? value)
         (not (map? value)))
    (vec value)

    (string? value)
    (or (parse-stringified-multi-value value)
        [value])

    :else
    (when (some? value)
      [value])))

(defn- option-label
  [field-schema value]
  (let [options (:options field-schema)]
    (or (when (seq options)
          (fu/find-option-label options value))
        (when (keyword? value)
          (name value))
        (some-> value str))))

(defn multi-value-formatter
  "Format a multivalued cell (JS array or CLJS collection) into a human-readable string."
  [value]
  (if-let [values (seq (normalize-multi-values value))]
    (->> values
         (remove nil?)
         (map str)
         (string/join "; "))
    (or (some-> value str) "")))

(defn keyword-formatter
  [field-schema value]
  (if-let [values (seq (normalize-multi-values value))]
    (->> values
         (remove nil?)
         (map (fn [item]
                (option-label field-schema item)))
         (string/join "; "))
    (or (option-label field-schema value) "")))

(defn percentage-formatter
  [value]
  (value-format/percentage-text value))

(defn multiplier-formatter
  [value]
  (value-format/multiplier-text value))

(defn- display-key
  [value]
  (cond
    (keyword? value)
    (name value)

    :else
    (some-> value str)))

(defn- summarize-structured-value
  [value]
  (cond
    (map? value)
    (->> value
         (take 3)
         (map (fn [[k v]]
                (str (display-key k) ": " (if (map? v)
                                            (pr-str v)
                                            (display-key v)))))
         (string/join "; "))

    (sequential? value)
    (->> value
         (take 3)
         (map display-key)
         (string/join "; "))

    :else
    (display-key value)))

(defn structured-value-formatter
  [value]
  (let [decoded (if (string? value)
                  (or (parse-edn-string value) value)
                  value)]
    (or (some-> decoded summarize-structured-value)
        "")))

(defn row-currency
  "The currency of a value in a row of the grid, as a keyword, or nil when the
   row has none. `row-js` is the row as AG Grid has it, a `_source` of the index
   with the fields under `flat`. `currency-flat-field` is the flat field of the
   currency, for example `flat.fund-commitment-cur-committed-amount--sym`. The
   index holds a keyword as a text with a colon, for example `:sek`."
  [{:keys [row-js currency-flat-field]}]
  (when (and row-js
             (string? currency-flat-field)
             (string/starts-with? currency-flat-field "flat."))
    (-> (some-> (aget row-js "flat")
                (aget (subs currency-flat-field (count "flat."))))
        currency/stored->currency)))

(defn- currency-format-args
  [{:keys [default-currency field-schema currency-flat-field params-js value]}]
  {:value             value
   :currency          (row-currency {:row-js              (oops/oget+ params-js "data")
                                     :currency-flat-field currency-flat-field})
   :default-currency  default-currency
   :number-multiplier (:field/number-multiplier field-schema)})

(defn currency-formatter
  "The text of a currency value: the label of the currency of the row, and the
   number. A row that has no currency gets `default-currency`."
  [args]
  (when (:value args)
    (value-format/currency-text (currency-format-args args))))

(defn currency-cell-parts
  "The label of the currency of the row and the number of a currency value, as
   `{:label .. :amount ..}`, for a cell that shows them apart. It has the same
   arguments as `currency-formatter`. A cell with no value gives nil."
  [args]
  (when (:value args)
    (value-format/currency-parts (currency-format-args args))))

(def ^:private right-aligned-data-types
  #{:data.type/currency-value
    :data.type/date
    :data.type/date-year
    :data.type/datetime
    :data.type/integer
    :data.type/number
    :data.type/percentage
    :data.type/multiplier})

(defn numeric-alignment-props
  "The column properties that put the header and the cells of a numeric column on
   the right, so that the digits of the rows are below each other. A date and a
   year are numeric too. The other columns have none."
  [data-type]
  (when (contains? right-aligned-data-types data-type)
    {:headerClass "ag-right-aligned-header"
     :cellClass   "ag-right-aligned-cell"}))

(defn col-value-formatter
  "AG Grid valueFormatter factory.
   Returns a formatter function that formats the cell value based on the field data type.
   `currency-flat-field` is for a currency value: the flat field that holds its currency.
   `date-format` is the date format of the tenant, for a date and a datetime. A date
   is shown in UTC, and a datetime in the time zone of the browser. Without a
   `date-format`, they have the format of the product."
  [{:keys [data-type default-currency currency-flat-field date-format field-schema]} params-js]
  (let [value (oops/oget params-js "value")]
    (case data-type
      :data.type/date
      (when value
        (or (value-format/date-text {:value       value
                                     :date-format date-format})
            (zap-time/long->formatted-date value)))

      :data.type/datetime
      (when value
        (or (value-format/datetime-text {:value       value
                                         :date-format date-format})
            (zap-time/long->formatted-date-time value)))

      :data.type/ref-multi
      (multi-value-formatter value)

      :data.type/email-multi
      (multi-value-formatter value)

      :data.type/text-multi
      (multi-value-formatter value)

      :data.type/url-multi
      (multi-value-formatter value)

      :data.type/keyword-multi
      (keyword-formatter field-schema value)

      :data.type/keyword
      (keyword-formatter field-schema value)

      :data.type/currency-value
      (currency-formatter {:default-currency    default-currency
                           :field-schema        field-schema
                           :currency-flat-field currency-flat-field
                           :params-js           params-js
                           :value               value})

      :data.type/currency-symbol
      (or (currencies/get-label value)
          (some-> value str)
          "")

      :data.type/percentage
      (percentage-formatter value)

      :data.type/multiplier
      (multiplier-formatter value)

      :data.type/text-edn
      (structured-value-formatter value)

      :data.type/financials
      (structured-value-formatter value)

      :data.type/tesi-financials
      (structured-value-formatter value)

      :data.type/tesi-financials-2
      (structured-value-formatter value)

      :data.type/number
      (value-format/number-text value)

      ;; Default: if AG Grid gives an array, display the first element.
      ;; Otherwise, return the value as-is (or empty string to avoid showing "null").
      (cond
        (js/Array.isArray value) (aget value 0)
        (nil? value) ""
        :else value))))

(defn get-associated-entity-type
  [associated-entity-field row-data-clj]
  (when associated-entity-field
    (let [value-key (-> associated-entity-field
                        (str ".original.entity/type")
                        (keyword))
          value-str (get row-data-clj value-key)]

      (keyword value-str))))

(defn cell-editable?
  [{:keys [editable-grid? pipeline-id field entity-type associated-entity-field] :as _props} params-js]
  (let [write-access? (if associated-entity-field
                        (let [row-data (oops/oget params-js "data")
                              row-data-clj (js->clj row-data :keywordize-keys true)
                              associated-entity-type (get-associated-entity-type associated-entity-field row-data-clj)]
                          (if-not associated-entity-type
                            false
                            (f-permissions/field-write-access? field associated-entity-type pipeline-id)))

                        (f-permissions/field-write-access? field entity-type pipeline-id))
        ;; TODO : fix this
        ;updating-some-cell? (get-in @state-atom [:grid :updating-cell?])
        editable? (and editable-grid?
                       write-access?
                       ;(not updating-some-cell?)
                       )]
    editable?))
